
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from './useAuthSession';
import { toast } from 'sonner';

// Define a type for the stats object to improve type safety
interface AdminStats {
  total_users?: number;
  active_users?: number;
  subscription_stats?: {
    active: number;
    trial: number;
  };
  revenue_stats?: {
    monthly: number;
    annual: number;
  };
}

export function useAdmin() {
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<AdminStats>({});
  const { userId, isAuthenticated, session } = useAuthSession();
  
  useEffect(() => {
    async function checkAdminStatus() {
      if (!isAuthenticated || !userId) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      try {
        // Special case for admin@admin.com in any environment
        if (session?.user?.email === 'admin@admin.com') {
          console.log('Admin access granted: admin@admin.com special case');
          setIsAdmin(true);
          
          // IMPORTANT: Ensure the admin profile exists to fix join operations in components
          try {
            // Check if profile exists
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', userId)
              .single();
              
            if (!existingProfile) {
              console.log('Creating missing admin profile');
              // Create profile if doesn't exist
              await supabase
                .from('profiles')
                .insert({
                  id: userId,
                  email: session.user.email,
                  full_name: 'Admin User'
                });
              
              console.log('Admin profile created successfully');
            }
            
            // Ensure admin role exists
            await supabase
              .from('user_roles')
              .upsert({
                user_id: userId,
                role: 'admin',
                is_admin: true,
                approved: true,
                approved_for_access: true,
                can_be_affiliate: true
              }, { 
                onConflict: 'user_id' 
              });
            
            console.log('Admin role created or updated successfully');
            
          } catch (profileError) {
            console.error('Error ensuring admin user setup:', profileError);
          }
          
          setIsLoading(false);
          return;
        }

        // Primary check: query the user_roles table directly
        const { data: userRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('is_admin')
          .eq('user_id', userId)
          .eq('is_admin', true)
          .maybeSingle();

        if (rolesError) {
          console.error('Error checking admin status from roles:', rolesError);
          
          // Fallback: try RPC function as backup
          try {
            const { data: isAdminResult, error: rpcError } = await supabase.rpc('is_admin_by_email');
            
            if (rpcError) {
              console.error('Error using is_admin_by_email RPC:', rpcError);
              // Final fallback - check email directly
              if (session?.user?.email === 'admin@admin.com') {
                console.log('Admin access granted via email fallback');
                
                // Create admin profile if doesn't exist
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', userId)
                  .maybeSingle();
                  
                if (!profile) {
                  await supabase
                    .from('profiles')
                    .insert({
                      id: userId,
                      email: session.user.email,
                      full_name: 'Admin User'
                    });
                }
                
                setIsAdmin(true);
              } else {
                setIsAdmin(false);
              }
            } else {
              setIsAdmin(!!isAdminResult);
            }
          } catch (err) {
            console.error('Error in RPC call:', err);
            // Final fallback - check email directly
            if (session?.user?.email === 'admin@admin.com') {
              console.log('Admin access granted via email after error');
              setIsAdmin(true);
            } else {
              setIsAdmin(false);
            }
          }
        } else {
          // Set admin status based on direct query result
          setIsAdmin(!!userRoles?.is_admin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        
        // Final fallback - check email directly
        if (session?.user?.email === 'admin@admin.com') {
          console.log('Admin access granted via email after exception');
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } finally {
        setIsLoading(false);
      }
    }
    
    checkAdminStatus();
  }, [userId, isAuthenticated, session]);

  // Fetch real stats from database if needed
  // For now we'll use mock stats as a placeholder
  const mockStats: AdminStats = {
    total_users: 1245,
    active_users: 892,
    subscription_stats: {
      active: 301,
      trial: 42
    },
    revenue_stats: {
      monthly: 124500,
      annual: 1494000
    }
  };

  return { isAdmin, isLoading, stats: mockStats };
}
